/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { AxisPosition, Group, TimeAxisProps, ChartSeriesData } from '#utils/UNSAFE_visTypes/chart';
import { Dimension } from '#utils/UNSAFE_visTypes/common';
import { getLocale } from '#utils/UNSAFE_getLocale';
import { GetTextDimensionsType } from '#hooks/PRIVATE_useTextDimensions';
import { merge } from '@oracle/oraclejet-internal-utilities/stringUtils';
import { getAxisMaxSize, getAxisRange, getSafeSkips } from '#PRIVATE_Axis/utils/axisUtils';
import { getTitleProps } from '#PRIVATE_Axis/utils/titleUtils';
import { AXIS_DEFAULTS } from '#PRIVATE_Axis/utils/axisDefaults';
import {
  getGroupAxisLabelsInfo,
  getMaxHeight,
  getMaxWidth,
  getTickLabelInfo,
  skipLabelsGreedy,
  updateLabelsDims
} from '#PRIVATE_Axis/utils/labelUtils';
import { applyInitialZooming } from '#utils/PRIVATE_chartUtils/overviewUtils';

const TIME_SECOND = 1000;
const TIME_MINUTE = 60 * TIME_SECOND;
const TIME_HOUR = 60 * TIME_MINUTE;
const TIME_DAY = 24 * TIME_HOUR;
const TIME_MONTH_MIN = 28 * TIME_DAY;
const TIME_MONTH_MAX = 31 * TIME_DAY;
const TIME_YEAR_MIN = 365 * TIME_DAY;
const TIME_YEAR_MAX = 366 * TIME_DAY;

export function getTimeAxisProps(
  groups: Group[],
  position: AxisPosition,
  availSpace: Dimension,
  viewportMin: number,
  viewportMax: number,
  averageInterval: number,
  isRtl: boolean,
  xAxis?: TimeAxisProps,
  axisFontSize?: string,
  getTextDimensions?: GetTextDimensionsType,
  isTimeAxis?: boolean
) {
  const isHoriz = position === 'top' || position === 'bottom';
  const isSize = xAxis?.size !== undefined;
  const axisMaxDim = getAxisMaxSize(availSpace, isHoriz, xAxis?.size, xAxis?.maxSize);
  const titleStyle = { fontSize: axisFontSize, ...xAxis?.titleStyle };
  const timeAxisType = xAxis?.timeAxisType || 'regular';
  const { titleDim, ...titleProps } = getTitleProps(
    xAxis?.title,
    titleStyle,
    axisMaxDim,
    availSpace,
    position,
    isRtl,
    getTextDimensions
  );
  const { width: titleWidth, height: titleHeight } = titleProps.dims!;
  const isTitleRendered = titleWidth > 0 && titleHeight > 0;
  let axisDim = isTitleRendered ? titleDim + AXIS_DEFAULTS.titleGap : 0;
  const isRotated = position === 'left' || position === 'right';

  if (isTitleRendered) {
    if (isRotated) {
      availSpace.width -= axisDim;
      availSpace.x += position === 'left' ? axisDim : 0;
    } else {
      availSpace.height -= axisDim;
      availSpace.y += position === 'top' ? axisDim : 0;
    }
  }
  const [start, end] = getAxisRange(availSpace, position, isRtl, isTimeAxis);

  const tickLabelStyle = {
    fontSize: axisFontSize,
    fill: xAxis?.tickLabel?.style?.color,
    ...xAxis?.tickLabel?.style,
    textAnchor: 'middle'
  };

  const valueToCoord = (value: number) => {
    return getCoordFromValue(
      value,
      viewportMin,
      viewportMax,
      start,
      end,
      averageInterval,
      groups,
      xAxis?.timeAxisType!
    );
  };

  const scale = {
    transform: valueToCoord,
    range: () => {
      return [start, end];
    },
    domain: () => {
      return [viewportMin, viewportMax];
    },
    invert: (coord: number) => {
      return getValueFromCoord(
        coord,
        start,
        end,
        viewportMin,
        viewportMax,
        averageInterval,
        groups,
        xAxis?.timeAxisType || 'enabled'
      );
    }
  };

  const locale = getLocale();
  let axisStep = xAxis?.step;

  let times: number[] = [];
  if (axisStep != null) {
    times = getLabelPos(viewportMin, viewportMax, axisStep);
  } else if (timeAxisType === 'mixedFrequency') {
    axisStep = getMixedFrequencyStep(viewportMax - viewportMin);
    times = getLabelPos(viewportMin, viewportMax, axisStep);
  } else {
    for (let i = 0; i < groups.length; i++) {
      const date = new Date(groups[i].id).getTime();
      if (date >= viewportMin && date <= viewportMax) times.push(date);
    }
    axisStep = averageInterval;
  }

  const level1Formatter = Array.isArray(xAxis?.tickLabel?.format)
    ? xAxis?.tickLabel?.format[0]
    : xAxis?.tickLabel?.format;
  const level2Formatter = Array.isArray(xAxis?.tickLabel?.format)
    ? xAxis?.tickLabel?.format[1]
    : undefined;

  if (timeAxisType != 'skipGaps') {
    const granularity = calculateGranularity(axisStep, viewportMax - viewportMin);
    times = treatMissingValues(times, granularity);

    if (times.length > 1) {
      // Since the contents of the times array might have been updated, the step value should
      // be updated.
      // The step value is approximated to the estimated average of the intervals in the updated
      // times array.
      // It is safe to do so even when there were no missing values as it should not have any side-effects
      // on rendering the axis.
      // If there are less than 2 values in the times array, proceed with the existing step value.
      axisStep = (times[times.length - 1] - times[0]) / (times.length - 1);
    }
    const label = formatAxisLabel(
      new Date(times[0] || viewportMin),
      undefined,
      !isHoriz,
      false,
      locale,
      granularity,
      level1Formatter,
      level2Formatter
    )[0];
    const labelDims = getTextDimensions?.(label!, tickLabelStyle);
    if (labelDims) {
      const labelDim = isHoriz ? labelDims.width : labelDims.height;
      const totalWidth =
        (labelDim + getTickLabelGap(labelDims.height, isHoriz)) * (times.length - 1);
      if (totalWidth > Math.abs(end - start)) {
        axisStep = getMixedFrequencyStep(viewportMax - viewportMin);
        times = getLabelPos(viewportMin, viewportMax, axisStep);
      }
    }
  }

  if (times.length === 0) {
    times = [viewportMin];
  }

  const granularity = calculateGranularity(axisStep!, viewportMax - viewportMin);

  const isLabelRendered = xAxis?.tickLabel?.isRendered != false;
  let isLabel1Rendered = isLabelRendered;
  let isLabel2Rendered = isLabelRendered;

  let tickLabels: any[] = [];
  const axisStepWidth = Math.abs(end - start) / times.length;

  if (xAxis?.isRendered === false) {
    return {
      defaultLabelFormatter: undefined,
      title: undefined,
      titleProps: undefined,
      titleStyle: {},
      axisDim: 0,
      scale,
      tickLabels: [],
      axisStepWidth,
      averageGroupZ: 0,
      tickLabelStyle: {},
      separators: [],
      isGroupLabelRendered: () => false
    };
  }

  if (isLabelRendered) {
    const { labelInfos1, labelInfos2 } = generateLabels(
      times,
      granularity,
      valueToCoord,
      level1Formatter,
      level2Formatter
    );

    const numOfSafeSkips = getSafeSkips(axisStepWidth, tickLabelStyle, getTextDimensions);

    const getLabel1Dims = (label: string | undefined, index: number) => {
      const rangeValue = labelInfos1[index].coord;
      if (!rangeValue || !label) {
        return {
          indexAtLevel: index,
          text: '',
          dims: { width: 0, height: 0, x: 0, y: 0 },
          lines: [],
          isTruncated: false,
          didNotFit: false,
          lineProps: []
        };
      }
      return getTickLabelInfo(
        rangeValue,
        label,
        index,
        tickLabelStyle,
        isHoriz,
        false,
        false,
        false,
        Infinity,
        Infinity,
        availSpace,
        getTextDimensions
      );
    };

    const getLabel2Dims = (label: string | undefined, index: number) => {
      const rangeValue = labelInfos2[index]?.coord;
      if (!rangeValue || !label) {
        return {
          indexAtLevel: index,
          text: '',
          dims: { width: 0, height: 0, x: 0, y: 0 },
          lines: [],
          lineProps: [],
          didNotFit: false,
          isTruncated: false
        };
      }

      return getTickLabelInfo(
        rangeValue,
        label,
        index,
        tickLabelStyle,
        isHoriz,
        false,
        false,
        false,
        Infinity,
        Infinity,
        availSpace,
        getTextDimensions
      );
    };

    const {
      maxWidth: label1MaxWidth,
      maxHeight: label1MaxHeight,
      tickLabels: tickLabels1
    } = getGroupAxisLabelsInfo(
      labelInfos1.map((i) => i.text),
      getLabel1Dims,
      isHoriz,
      false,
      false,
      numOfSafeSkips
    );

    axisDim = isHoriz ? label1MaxHeight : label1MaxWidth;

    isLabel1Rendered =
      isTitleRendered && isHoriz
        ? axisDim < axisMaxDim - titleHeight
        : axisDim < axisMaxDim - titleHeight;

    const {
      maxWidth: label2MaxWidth,
      maxHeight: label2MaxHeight,
      tickLabels: tickLabels2
    } = skipLabelsGreedy(
      labelInfos2.map((i) => i.label2),
      getLabel2Dims,
      isHoriz
    );

    const level2AxisDim = isHoriz ? axisDim + label2MaxHeight : axisDim + label2MaxWidth;

    isLabel2Rendered = isHoriz
      ? level2AxisDim < axisMaxDim - titleHeight
      : level2AxisDim < axisMaxDim - titleHeight;

    if (isLabel1Rendered) {
      const label1MaxW = getMaxWidth(isSize, position, axisMaxDim, label1MaxWidth, titleDim);
      const label1MaxH = getMaxHeight(isSize, position, axisMaxDim, label1MaxHeight, titleDim);
      const getAxisPos = (index: number) => {
        return labelInfos1[index].coord;
      };
      updateLabelsDims(label1MaxW, label1MaxH, tickLabels1, getAxisPos, availSpace, position);
      tickLabels = tickLabels.concat(tickLabels1);
      const label1Dims = isHoriz ? label1MaxH : label1MaxW;
      axisDim = axisDim + titleHeight + label1Dims;
    }

    if (isLabel2Rendered) {
      const label2MaxW = getMaxWidth(isSize, position, axisMaxDim, label2MaxWidth, titleDim);
      const label2MaxH = getMaxHeight(isSize, position, axisMaxDim, label2MaxHeight, titleDim);
      const getAxisPos = (index: number) => {
        return labelInfos2[index].coord;
      };
      updateLabelsDims(label2MaxW, label2MaxH, tickLabels2, getAxisPos, availSpace, position);
      tickLabels.forEach((value) => {
        if (value.lineProps[0]) {
          value.lineProps[0].y -= value.lineProps[0].height;
        }
      });
      tickLabels = tickLabels.concat(tickLabels2);
      const label2Dims = isHoriz ? label2MaxH : label2MaxW;
      axisDim = level2AxisDim + titleHeight + label2Dims;
    }
  }

  const defaultLabelFormatter = (axisValue: number) => {
    const date = new Date(axisValue);
    const twoLabels = formatAxisLabel(
      date,
      undefined,
      false,
      false,
      locale,
      granularity,
      level1Formatter,
      level2Formatter
    );
    return merge(twoLabels as string[]) as string;
  };

  return {
    defaultLabelFormatter,
    title: isTitleRendered ? xAxis?.title : undefined,
    titleProps: isTitleRendered ? titleProps : undefined,
    titleStyle,
    axisDim,
    scale,
    tickLabels,
    axisStepWidth,
    averageGroupZ: 0,
    tickLabelStyle,
    separators: [],
    isGroupLabelRendered: () => false
  };
}

/**
 * Calculates the granularity of the time axis.
 * @param step The step of the axis.
 * @param timeRange The time range of the axis.
 * @returns
 */
export function calculateGranularity(step: number, timeRange: number) {
  if (step >= TIME_YEAR_MIN || timeRange >= 6 * TIME_YEAR_MIN) return TIME_YEAR_MIN;

  if (step >= TIME_MONTH_MIN || timeRange >= 6 * TIME_MONTH_MIN) return TIME_MONTH_MIN;

  if (step >= TIME_DAY || timeRange >= 6 * TIME_DAY) return TIME_DAY;

  if (step >= TIME_HOUR || timeRange >= 6 * TIME_HOUR) return TIME_HOUR;

  if (step >= TIME_MINUTE || timeRange >= 6 * TIME_MINUTE) return TIME_MINUTE;

  return TIME_SECOND;
}

/**
 * Returns the step for mixed frequency time range.
 * @param timeRange
 * @returns
 */
export function getMixedFrequencyStep(timeRange: number) {
  if (timeRange >= 6 * TIME_YEAR_MIN) return TIME_YEAR_MIN;
  if (timeRange >= 6 * TIME_MONTH_MIN) return TIME_MONTH_MIN;
  if (timeRange >= 6 * TIME_DAY) return TIME_DAY;
  if (timeRange >= TIME_DAY) return 3 * TIME_HOUR;
  if (timeRange >= 6 * TIME_HOUR) return TIME_HOUR;
  if (timeRange >= TIME_HOUR) return 15 * TIME_MINUTE;
  if (timeRange >= 30 * TIME_MINUTE) return 5 * TIME_MINUTE;
  if (timeRange >= 6 * TIME_MINUTE) return TIME_MINUTE;
  if (timeRange >= TIME_MINUTE) return 15 * TIME_SECOND;
  if (timeRange >= 30 * TIME_SECOND) return 5 * TIME_SECOND;
  return TIME_SECOND;
}

export function formatLabelWithFormatter(
  date: Date,
  prevDate: Date | undefined,
  formatter?: (date: number) => string
) {
  if (!formatter) {
    return '';
  }

  const label = formatter(date.getTime());
  const prevLabel = prevDate ? formatter?.(prevDate.getTime()) : undefined;

  if (prevLabel !== label || label == null) {
    return label;
  }
  // TODO: JET-65541. This is not as straightforward for format function. See old jet logic formatLabelWithConverter.
  // do not skip label when it is same as prevLabel checking for granularity
  // for eg. if June and July both convert to 'J' and granularity is TIME_MONTH_MIN, we want to keep both 'J's
  // for eg. if Saturday and Sunday both convert to 'S' and granularity is TIME_MONTH_DAY, we want to keep both 'S's

  return '';
}

export function formatAxisLabel(
  date: Date,
  prevDate: Date | undefined,
  bOneLabel: boolean,
  isVert: boolean,
  locale: string,
  granularity: number,
  label1Formatter?: (date: number) => string,
  label2Formatter?: (date: number) => string
) {
  let label1 = null; // level 1 label
  let label2 = null; // level 2 label

  // If dateTimeFormatter is used, use it
  if (label1Formatter || label2Formatter) {
    if (label1Formatter) label1 = formatLabelWithFormatter(date, prevDate, label1Formatter);
    if (label2Formatter) label2 = formatLabelWithFormatter(date, prevDate, label2Formatter);

    return [label1, label2];
  }

  if (granularity === TIME_YEAR_MIN) {
    label1 = formatDate(date, locale, false, false, true); // Year
  } else if (granularity === TIME_MONTH_MIN) {
    if (prevDate == null || prevDate.getMonth() != date.getMonth())
      label1 = formatDate(date, locale, false, true, false); // Month

    if (prevDate == null || prevDate.getFullYear() != date.getFullYear())
      label2 = formatDate(date, locale, false, false, true); // Year
  } else if (granularity === TIME_DAY) {
    if (bOneLabel) {
      label1 = formatDate(date, locale, true, true, true); // Day, Month, Year
    } else {
      if (prevDate == null || prevDate.getDate() != date.getDate())
        label1 = formatDate(date, locale, true, false, false); // Day

      if (prevDate == null || prevDate.getFullYear() != date.getFullYear())
        label2 = formatDate(date, locale, false, true, true); // Year, Month
      else if (prevDate.getMonth() != date.getMonth())
        label2 = formatDate(date, locale, false, true, false); // Month
    }
  } else {
    if (granularity === TIME_HOUR) {
      if (prevDate == null || prevDate.getHours() != date.getHours())
        label1 = formatTime(date, locale, false, false); // HH AM/PM or HH:MM
    } else if (granularity === TIME_MINUTE) {
      if (prevDate == null || prevDate.getMinutes() != date.getMinutes())
        label1 = formatTime(date, locale, true, false); // HH:MM
    } else {
      if (prevDate == null || prevDate.getSeconds() != date.getSeconds())
        label1 = formatTime(date, locale, true, true); // HH:MM:SS
    }

    if (isVert) {
      if (prevDate == null || prevDate.getDate() != date.getDate())
        label2 = formatDate(date, locale, true, true, false); // Month, Day
    } else {
      if (prevDate == null || prevDate.getFullYear() != date.getFullYear())
        label2 = formatDate(date, locale, true, true, true); // Year, Month, Day
      else if (prevDate.getMonth() != date.getMonth())
        label2 = formatDate(date, locale, true, true, false); // Month, Day
      else if (prevDate.getDate() != date.getDate())
        label2 = formatDate(date, locale, true, false, false); // Day
    }
  }

  return [label1, label2];
}

/**
 * Returns the formatted date string.
 * @param date
 * @param locale
 * @param showDay
 * @param showMonth
 * @param showYear
 * @returns
 */
export function formatDate(
  date: Date,
  locale: string,
  showDay: boolean,
  showMonth: boolean,
  showYear: boolean
) {
  // this function is equivalent to _formatDate present in DvtTimeAxisInfo
  // replacing the impl to use toLocaleDateString

  const options = {
    year: showYear ? ('numeric' as const) : undefined,
    month: showMonth ? ('short' as const) : undefined,
    day: showDay ? ('numeric' as const) : undefined
  };

  return date.toLocaleDateString(locale, options);
}

/**
 * Returns the formatted time string.
 * @param date
 * @param locale
 * @param showMinute
 * @param showSecond
 * @returns
 */
export function formatTime(date: Date, locale: string, showMinute: boolean, showSecond: boolean) {
  // this function is equivalent to _formatDate present in DvtTimeAxisInfo
  // replacing the impl to use toLocaleDateString

  const options = {
    hour: '2-digit' as const,
    minute: showMinute ? ('2-digit' as const) : undefined,
    second: showSecond ? ('2-digit' as const) : undefined
  };

  return date.toLocaleTimeString(locale, options);
}

export function getLabelPos(start: number, end: number, step: number) {
  // The time positions has to be at even intervals from the beginning of a year (January 1, 12:00:00 AM), otherwise
  // we may have labels such as [2013, 2014, 2015, ...] that are drawn at [June 8 2013, June 8 2014, June 8 2015, ...],
  // which is data misrepresentation.
  const anchor = new Date(start);
  const initialTimezoneOffset = anchor.getTimezoneOffset();
  anchor.setMonth(0, 1); // January 1
  anchor.setHours(0, 0, 0, 0); // 00:00:00
  let time = anchor.getTime();

  const times = [];
  if (step >= TIME_YEAR_MIN && step <= TIME_YEAR_MAX) {
    // Assume that the step is one year, which can mean different # of days depending on the year
    while (time < start) time = addOneYear(time);
    while (time <= end) {
      times.push(time);
      time = addOneYear(time);
    }
  } else if (step >= TIME_MONTH_MIN && step <= TIME_MONTH_MAX) {
    // Assume that the step is one month, which can mean different # of days depending on the month
    while (time < start) time = addOneMonth(time);
    while (time <= end) {
      times.push(time);
      time = addOneMonth(time);
    }
  } else {
    // Bug 26396791. Correction is needed due to daylight savings.
    // Only apply daylight correction when step is less than a month. Daylight savings does not impact any step higher than month.
    // JET-52348 - Ideally we should be using Date api to add and substract date offsets to calculate labels since
    // Date will automatically handle daylight savings. This approach should solve issues for steps greater than month but we could encounter
    // offsets in axis labels when dataset values are in different daylight savings and chart step is low (eg day or hour);
    const timezoneCorrection = (initialTimezoneOffset - anchor.getTimezoneOffset()) * 60 * 1000;
    const correction = step < TIME_MONTH_MIN ? timezoneCorrection : 0;
    time += Math.ceil((start - time - correction) / step) * step + correction;
    while (time <= end) {
      times.push(time);
      time += step;
    }
  }
  return times;
}

export function generateLabels(
  times: number[],
  granularity: number,
  getCoordFromValue: (time: number) => number,
  label1Formatter?: (time: number) => string,
  label2Formatter?: (time: number) => string
) {
  const locale = getLocale();

  const labelInfos1 = [];
  const coords1 = [];

  const labelInfos2 = [];
  const coords2 = [];
  let isOneLevel = true;
  let prevDate = undefined;
  for (let i = 0; i < times.length; i++) {
    const time = times[i];
    const coord = getCoordFromValue(time);
    const date = new Date(time);
    const [label1, label2] = formatAxisLabel(
      date,
      prevDate,
      false,
      false,
      locale,
      granularity,
      label1Formatter,
      label2Formatter
    );
    if (label1 != null) {
      labelInfos1.push({ text: label1, coord });
      coords1.push(coord);
    }

    if (label2 != null) {
      labelInfos2.push({ label2, coord });
      coords2.push(coord);
      isOneLevel = false;
    }

    prevDate = date;
  }
  return {
    labelInfos1,
    labelInfos2,
    coords1,
    coords2,
    isOneLevel
  };
}
/**
 * Adds the time by one year, e.g. 2014 January 15 -> 2015 January 15 -> ...
 */
export function addOneYear(time: number) {
  const date = new Date(time);
  date.setFullYear(date.getFullYear() + 1);
  return date.getTime();
}

/**
 * Adds the time by one month, e.g. January 15 -> February 15 -> March 15 -> ...
 */
export function addOneMonth(time: number) {
  const date = new Date(time);
  date.setMonth(date.getMonth() + 1);
  return date.getTime();
}

/**
 * Returns the corresponding index for the given time in time axis.
 * @param time The time stamp.
 * @param averageInterval The average interval of the time axis.
 * @param groups The groups array.
 * @returns
 */
export function timeToIndex(time: number, averageInterval: number, groups: Group[]) {
  let endIndex = groups.length;
  // TODO: can do binary search to make this fast
  for (let i = 0; i < groups.length; i++) {
    if (time <= new Date(groups[i].id).getTime()) {
      endIndex = i;
      break;
    }
  }
  const startIndex = endIndex - 1;

  const startTime =
    groups[startIndex] !== undefined
      ? new Date(groups[startIndex].id).getTime()
      : new Date(groups[0].id).getTime() - averageInterval;
  const endTime =
    groups[endIndex] !== undefined
      ? new Date(groups[endIndex].id).getTime()
      : new Date(groups[groups.length - 1].id).getTime() + averageInterval;

  return startIndex + (time - startTime) / (endTime - startTime);
}

/**
 * Returns the time stamp for given index.
 * @param index The index.
 * @param averageInterval The average interval of the axis.
 * @param groups The groups array.
 * @returns
 */
export function indexToTime(index: number, averageInterval: number, groups: Group[]) {
  const endIndex = Math.min(Math.max(Math.ceil(index), 0), groups.length);
  const startIndex = endIndex - 1;
  const startTime =
    groups[startIndex] !== undefined
      ? getTime(groups[startIndex].id)
      : getTime(groups[0].id) - averageInterval;
  const endTime =
    groups[endIndex] !== undefined
      ? getTime(groups[endIndex].id)
      : getTime(groups[groups.length - 1].id) + averageInterval;

  return startTime + (index - startIndex) * (endTime - startTime);
}

export function getTime(uxTime: string | number) {
  return new Date(uxTime).getTime();
}

export function getCoordFromValue(
  value: number,
  minValue: number,
  maxValue: number,
  startCoord: number,
  endCoord: number,
  averageInterval: number,
  groups: Group[],
  timeAxisType: 'skipGaps' | 'mixedFrequency' | 'enabled'
) {
  let ratio;
  if (timeAxisType === 'skipGaps') {
    if (groups.length < 1) {
      return startCoord;
    }
    const minVal = timeToIndex(minValue, averageInterval, groups);
    const maxVal = timeToIndex(maxValue, averageInterval, groups);
    const val = timeToIndex(value, averageInterval, groups);
    ratio = (val - minVal) / (maxVal - minVal);
  } else {
    ratio = (value - minValue) / (maxValue - minValue);
  }

  return startCoord + ratio * (endCoord - startCoord);
}

export function getValueFromCoord(
  coord: number,
  startCoord: number,
  endCoord: number,
  minValue: number,
  maxValue: number,
  averageInterval: number,
  groups: Group[],
  timeAxisType: 'skipGaps' | 'enabled' | 'mixedFrequency'
) {
  const ratio = (coord - startCoord) / (endCoord - startCoord);

  if (timeAxisType === 'skipGaps') {
    const minVal = timeToIndex(minValue, averageInterval, groups);
    const maxVal = timeToIndex(maxValue, averageInterval, groups);
    return indexToTime(minVal + ratio * (maxVal - minVal), averageInterval, groups);
  } else return minValue + ratio * (maxValue - minValue);
}

export function isoToNumber(dateString?: string) {
  if (!dateString) {
    return;
  }
  return new Date(dateString).getTime();
}

/**
 * Returns the average interval for the time axis.
 * @param groups The number of groups.
 * @param start The earliest time among all data points.
 * @param end The latest time among all data points.
 * @returns
 */
export function getAverageInterval(groups: Group[], start: number, end: number) {
  if (end != start && groups.length > 1) {
    return (end - start) / (groups.length - 1);
  } else if (end - start > 0) {
    return end - start;
  }

  return 6 * TIME_MINUTE;
}

/**
 * Returns the time axis info.
 * @param groups The groups array.
 * @param xAxis The xAxis props.
 * @param offset The start and end offset for the axis.
 * @param timeAxisRange The start and end of the time range.
 * @returns
 */
export function getTimeAxisInfo(
  groups: Group[],
  xAxis: TimeAxisProps = {},
  startOffset: number,
  endOffset: number,
  timeAxisRange: { start?: number; end?: number },
  isHoriz: boolean,
  availSpace: Dimension,
  zoomAndScroll?: 'live' | 'off',
  initialZooming?: 'first' | 'last' | 'none',
  getTextDimensions?: GetTextDimensionsType
) {
  let { start, end } = timeAxisRange;
  const { viewportMin, viewportMax, viewportEndGroup, viewportStartGroup } = xAxis;

  const numGroups = groups.length;

  let minValue = isoToNumber((viewportMin || viewportStartGroup) as string);
  let maxValue = isoToNumber((viewportMax || viewportEndGroup) as string);

  const axisType = xAxis.timeAxisType!;
  // if start or end are undefined, use the application set viewport settings
  if (start == undefined) {
    start = minValue;
  }

  if (end == undefined) {
    end = maxValue;
  }

  const averageInterval = getAverageInterval(groups, start!, end!);

  const globalMin = start! + startOffset * averageInterval;
  const globalMax = end! + endOffset * averageInterval;

  const xAxisFontStyle = { ...xAxis?.tickLabel?.style };

  const { min, max } = applyInitialZooming({
    zoomAndScroll,
    initialZooming,
    currentMin: minValue,
    currentMax: maxValue,
    globalMin,
    globalMax,
    startOffset,
    endOffset,
    numGroups: numGroups,
    availSpace,
    isHoriz,
    getTextDimensions,
    tickLabelStyle: xAxisFontStyle
  });
  minValue = min ?? minValue;
  maxValue = max ?? maxValue;

  minValue = minValue || globalMin;
  maxValue = maxValue || globalMax;

  let startIndex = 0;
  let endIndex = Math.max(0, groups.length - 1);

  // TODO: since groups is expected to be sorted, can binary search here
  groups.forEach((group: Group, index: number) => {
    const timeStamp = new Date(group.id).getTime();

    if (timeStamp < minValue!) {
      startIndex = index;
    }

    if (timeStamp < maxValue!) {
      endIndex = index;
    }
  });

  const minAxisExtent = getMinTimeAxisExtent(axisType, globalMin, globalMax, averageInterval);

  return {
    viewportMin: minValue,
    viewportMax: maxValue,
    viewportStartGroup,
    viewportEndGroup,
    startIndex,
    endIndex,
    min: globalMin,
    max: globalMax,
    step: averageInterval,
    ticks: [],
    minorTicks: [],
    minAxisExtent
  };
}

function getMinTimeAxisExtent(
  axisType: 'skipGaps' | 'mixedFrequency' | 'enabled',
  globalMin: number,
  globalMax: number,
  averageInterval: number
) {
  if (axisType === 'skipGaps') {
    return 1;
  } else if (axisType === 'mixedFrequency') {
    return Math.min((globalMax - globalMin) / 8, averageInterval);
  } else {
    return averageInterval;
  }
}

/**
 * Returns the maximum of time stamps for the given dataset
 * and time axis type.
 * @param groups The array of groups.
 * @param series The array of series.
 * @param getDataItem The function that returns item for given seriesIndex and groupIndex.
 * @param timeAxisType The time axis type.
 * @returns
 */
export function getStartAndEndTime<K extends string | number, D extends { x?: number | string }>(
  groups: Group[],
  series: ChartSeriesData<K, D>[],
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  timeAxis: TimeAxisProps
) {
  const range: { start?: number; end?: number } = {
    start: Number.MAX_VALUE,
    end: -Number.MAX_VALUE
  };
  const numGroups = groups.length;
  const timeAxisType = timeAxis.timeAxisType;

  if (timeAxisType != 'mixedFrequency' && numGroups > 0) {
    range.start = isoToNumber(groups[0].id) as number;
    range.end = isoToNumber(groups[numGroups - 1].id) as number;
  } else {
    // TODO: this is inefficient. We should precompute all the repeated necessary
    // infomation and reuse whereever needed.
    series.forEach((_: ChartSeriesData<K, D>, seriesIndex: number) => {
      groups.forEach((_: Group, groupIndex) => {
        const itemX = getDataItem(seriesIndex, groupIndex)?.x;
        if (typeof itemX == 'number') return;
        const num = isoToNumber(itemX);
        if (num) {
          range.start = Math.min(range.start!, num);
          range.end = Math.max(range.end!, num);
        }
      });
    });
  }
  // if we can't determine valid range, let the application set viewportMin and viewportMax
  // or viewportStartGroup, viewportEndGroup to determine the axis range.
  if (range.start === Number.MAX_VALUE && range.end === -Number.MAX_VALUE) {
    range.start = undefined;
    range.end = undefined;
  }
  return range;
}

/**
 * Treat missing values when there are large gaps in time axis.
 * @param times
 * @param granularity
 * @returns
 */
function treatMissingValues(times: number[], granularity: number) {
  let initialInterval;
  const intervals: Set<number> = new Set();
  let hasMissingValues = false;
  let ret = times; // If there are no missing values, the original array will be returned.
  const intervalsCache = [];

  const timeLength = times.length;
  let previousDate = new Date(times[0]);
  for (let i = 1; i < timeLength; i++) {
    // Calculate the current interval.
    const currentDate = new Date(times[i]);
    const currentInterval = calculateGranularInterval(previousDate, currentDate, granularity);
    previousDate = currentDate;

    // Cache the result so as to not calculate again while filling the missing values
    // Since the cache is only used for filling in the missing values, and the operation
    // is performed in the same order as this one, having the cache key in the format
    // '1546318800000-1514782800000' should be fine.
    intervalsCache.push(currentInterval);

    // Proceed only if the current interval is greater than the current
    // granularity level.
    if (currentInterval <= 0) {
      continue;
    }

    // Add the current interval to the set.
    // The GCD of all the intervals will be used to fill in the missing the
    // values.
    intervals.add(currentInterval);

    // During the initial iteration, we will not have any information on intervals,
    // so, store the current interval and continue.
    if (!initialInterval) {
      initialInterval = currentInterval;
      continue;
    }

    // Check if the current interval is regular.
    if (currentInterval !== initialInterval) {
      hasMissingValues = true;
    }
  }

  // If missing values are present, treat them.
  if (hasMissingValues) {
    // Get the GCD of intervals and it will be the minimum interval in the new set of times
    const minimumInterval = getGCDInterval([...intervals]);
    ret = [];
    ret.push(times[0]);
    for (let i = 1; i < timeLength; i++) {
      const currentValue = times[i];
      let previousValue = times[i - 1];

      // Retrieve the interval from cache.
      // As we are looping through the same array the cache will have the interval value
      // and will never be undefined.
      const currentInterval = intervalsCache[i - 1];

      // If no values are missing in this interval,
      // add current value to the return array and continue.
      // Note: Values are considered missing only when the interval is greater than the current granularity level (minimumInterval).
      // Example: Jan 1, Jan 15, Feb 1, Mar 1, May 1, Jun 1
      // Granularity = Month; Minimum Interval = 1 (1 month)
      // Mar 1, May 1 => has one missing value: Apr
      // Jan 1, Jan 15 => has no missing value
      if (currentInterval <= minimumInterval) {
        ret.push(currentValue);
        continue;
      }

      // Calculate the interval at which the values are to be filled
      const ratioOfCurrentIntervalToMinimumInterval = currentInterval / minimumInterval; // Should be a round number as minimumInterval is a divisor of currentInterval
      const numMissingValues = ratioOfCurrentIntervalToMinimumInterval - 1;
      const fillIntervalInMilliseconds = Math.round(
        (currentValue - previousValue) / ratioOfCurrentIntervalToMinimumInterval
      );

      // Fill in the missing values
      for (let j = 1; j <= numMissingValues; j++) {
        previousValue += fillIntervalInMilliseconds;
        ret.push(previousValue);
      }
      ret.push(currentValue); // Finally, add the current value
      previousValue = currentValue; // Update the previous value
    }
  }
  return ret;
}

/**
 * Calculates the granual interval.
 * @param previousDate
 * @param currentDate
 * @param granularity
 * @returns
 */
function calculateGranularInterval(previousDate: Date, currentDate: Date, granularity: number) {
  // Treat times for the day light savings.
  const prevTimezoneOffset = previousDate.getTimezoneOffset();
  const currentTimezoneOffset = currentDate.getTimezoneOffset();
  const currentTime = currentDate.getTime();
  const currentTimezoneOffsetCorrection = (prevTimezoneOffset - currentTimezoneOffset) * 1000 * 60;
  if (currentTimezoneOffsetCorrection > 0) {
    currentDate.setTime(currentTime + currentTimezoneOffsetCorrection);
  }

  let granularInterval;
  if (granularity === TIME_YEAR_MIN)
    granularInterval = currentDate.getFullYear() - previousDate.getFullYear();
  else if (granularity === TIME_MONTH_MIN)
    granularInterval =
      12 * (currentDate.getFullYear() - previousDate.getFullYear()) +
      (currentDate.getMonth() - previousDate.getMonth());
  else
    granularInterval = Math.round((currentDate.getTime() - previousDate.getTime()) / granularity);

  if (currentTimezoneOffsetCorrection > 0) {
    currentDate.setTime(currentTime); // reset to original value
  }

  return granularInterval;
}

/**
 * Returns the greatest common denominator.
 * @param interval1
 * @param interval2
 * @returns
 */
function gcd(interval1: number, interval2: number): number {
  if (interval1 === 0) return interval2;
  return gcd(interval2 % interval1, interval1);
}

/**
 * Returns the gcd of the intervals.
 * @param intervals
 * @returns
 */
function getGCDInterval(intervals: number[]) {
  let result = intervals[0];
  for (let i = 1; i < intervals.length; i++) {
    result = gcd(result, intervals[i]);
    if (result === 1) return 1;
  }
  return result;
}

/**
 * Retuns the tick label gap.
 * @param labelHeight The height of the label.
 * @param isHoriz Whether the axis is horizontal.
 * @returns
 */
function getTickLabelGap(labelHeight: number, isHoriz: boolean) {
  // legacy numbers.
  return isHoriz ? labelHeight * 0.79 : labelHeight * 0.28;
}
